import { CheckCircle, Clock, FlaskConical, Rocket } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { CadrageStatusBreakdown } from '../../types/dashboard';


interface CadrageStatusCardProps {
    data: CadrageStatusBreakdown;
}

export const CadrageStatusCard: React.FC<CadrageStatusCardProps> = ({ data }) => {
    const { t } = useTranslation();

    const statusItems = [
        { key: 'draft', value: data.draft, icon: Clock, color: 'text-gray-500', bg: 'bg-gray-100' },
        { key: 'validated', value: data.validated, icon: CheckCircle, color: 'text-blue-500', bg: 'bg-blue-100' },
        { key: 'planned', value: data.planned, icon: Clock, color: 'text-yellow-500', bg: 'bg-yellow-100' },
        { key: 'progress', value: data.progress, icon: FlaskConical, color: 'text-orange-500', bg: 'bg-orange-100' },
        { key: 'forge', value: data.forge, icon: FlaskConical, color: 'text-purple-500', bg: 'bg-purple-100' },
        { key: 'deployed', value: data.deployed, icon: Rocket, color: 'text-green-500', bg: 'bg-green-100' },
    ];

    return (
        <div className="bg-card border border-border rounded-xl p-6">
            <h3 className="text-lg font-semibold text-foreground mb-4">
                {t('dashboard.cadrageStatus.title', 'Solution Status Breakdown')}
            </h3>

            <div className="space-y-3">
                {statusItems.map(({ key, value, icon: Icon, color, bg }) => {
                    const percentage = data.total > 0 ? Math.round((value / data.total) * 100) : 0;

                    return (
                        <div key={key} className="flex items-center justify-between">
                            <div className="flex items-center gap-3 flex-1">
                                <div className={`p-2 rounded-lg ${bg}`}>
                                    <Icon className={`h-5 w-5 ${color}`} />
                                </div>
                                <div className="flex-1">
                                    <div className="flex items-center justify-between mb-1">
                                        <span className="text-sm font-medium text-foreground">
                                            {t(`dashboard.cadrageStatus.${key}`, key.charAt(0).toUpperCase() + key.slice(1))}
                                        </span>
                                        <span className="text-sm text-muted-foreground">{value}</span>
                                    </div>
                                    <div className="w-full bg-muted rounded-full h-2">
                                        <div
                                            className={`h-2 rounded-full transition-all duration-300 ${color.replace('text-', 'bg-')}`}
                                            style={{ width: `${percentage}%` }}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            <div className="mt-4 pt-4 border-t border-border">
                <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-foreground">
                        {t('dashboard.cadrageStatus.total', 'Total')}
                    </span>
                    <span className="text-lg font-bold text-primary">{data.total}</span>
                </div>
            </div>
        </div>
    );
};
